/* Oshea menu drawer — takes over the theme's mobile hamburger.
 *
 * The theme's own drawer (#MenuDrawer) is left in the DOM but never opened:
 * we intercept the hamburger in the capture phase so the theme's handler
 * never runs, and the stylesheet keeps #MenuDrawer hidden below 1024px.
 */
if (!customElements.get('oshea-menu')) {
  const OMX_DESKTOP = 1024;

  class OsheaMenu extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.omx__panel');
      this.body = this.querySelector('[data-omx-body]');
      this.lastFocus = null;

      this.addEventListener('click', this.onClick.bind(this));
      this.onKeydown = this.onKeydown.bind(this);

      // Capture-phase so the theme's own drawer handler never fires.
      this.onTriggerClick = this.onTriggerClick.bind(this);
      document.addEventListener('click', this.onTriggerClick, true);

      // Keep only one accordion open per level — closer to the reference design.
      this.querySelectorAll('details[data-omx-acc]').forEach((d) => {
        d.addEventListener('toggle', () => {
          if (!d.open) return;
          const siblings = d.parentElement ? d.parentElement.children : [];
          Array.from(siblings).forEach((sib) => {
            if (sib !== d && sib.tagName === 'DETAILS' && sib.hasAttribute('data-omx-acc')) sib.open = false;
          });
        });
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onTriggerClick, true);
      document.removeEventListener('keydown', this.onKeydown);
    }

    isTrigger(el) {
      const btn = el.closest('.menu-drawer-button[aria-controls="MenuDrawer"], [data-omx-open]');
      if (!btn) return null;
      // The desktop "toggle navigation" button has no aria-controls — leave it alone.
      if (window.innerWidth >= OMX_DESKTOP && !btn.hasAttribute('data-omx-open')) return null;
      return btn;
    }

    onTriggerClick(e) {
      const btn = this.isTrigger(e.target);
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      this.open(btn);
    }

    open(trigger) {
      this.lastFocus = trigger || document.activeElement;
      this.hidden = false;
      this.syncCartCount();
      document.documentElement.classList.add('omx-lock');
      void this.offsetWidth; // flush layout so the slide-in has a start value
      this.classList.add('is-open');
      document.addEventListener('keydown', this.onKeydown);
      this.querySelector('.omx__close')?.focus({ preventScroll: true });
      this.track('menu_open');
    }

    close() {
      if (this.hidden) return;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('omx-lock');
      document.removeEventListener('keydown', this.onKeydown);
      setTimeout(() => {
        this.hidden = true;
        if (this.body) this.body.scrollTop = 0;
      }, 280);
      this.lastFocus?.focus?.({ preventScroll: true });
    }

    onKeydown(e) {
      if (e.key === 'Escape') this.close();
    }

    onClick(e) {
      if (e.target.closest('[data-omx-close]')) {
        e.preventDefault();
        this.close();
        return;
      }

      if (e.target.closest('[data-omx-search]')) {
        e.preventDefault();
        this.close();
        // Prefer the header's own predictive search; fall back to the search page.
        setTimeout(() => {
          const input = document.querySelector('predictive-search input[type="search"]');
          if (input) {
            input.focus();
            input.dispatchEvent(new Event('focus', { bubbles: true }));
          } else {
            window.location.href = `${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}search`;
          }
        }, 300);
        return;
      }

      const link = e.target.closest('a[data-omx-link]');
      if (link) {
        this.track('menu_click', { menu_item: link.textContent.trim().slice(0, 60), menu_url: link.getAttribute('href') });
        this.close();
      }
    }

    syncCartCount() {
      const badge = this.querySelector('[data-omx-cart-count]');
      if (!badge) return;
      const live = document.querySelector('.cart-count, [data-cart-count], .header__cart-count');
      const n = live ? parseInt(live.textContent.trim(), 10) : NaN;
      if (!Number.isNaN(n)) badge.textContent = n;
      badge.hidden = Number(badge.textContent) === 0;
    }

    track(event, params) {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({ event }, params));
      } catch (e) {}
    }
  }

  customElements.define('oshea-menu', OsheaMenu);
}

/* Product page, mobile only: collapse the header search behind an icon that
 * sits beside the hamburger. Injected at runtime so sections/header.liquid
 * stays untouched. */
(() => {
  const init = () => {
    if (!document.body.classList.contains('product-template')) return;

    const holder = document.querySelector('.header__icons--left .header__buttons');
    const wrap = document.querySelector('.header__search');
    if (!holder || !wrap || holder.querySelector('[data-ohs-toggle]')) return;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ohs-btn';
    btn.dataset.ohsToggle = '';
    btn.setAttribute('aria-label', 'Search');
    btn.setAttribute('aria-expanded', 'false');
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor" ' +
      'stroke-width="1.8" stroke-linecap="round" aria-hidden="true">' +
      '<circle cx="11" cy="11" r="7"></circle><path d="m20 20-3.5-3.5"></path></svg>';
    holder.appendChild(btn);

    const field = () => wrap.querySelector('input[type="search"]');
    const isOpen = () => document.body.classList.contains('ohs-open');

    const open = () => {
      document.body.classList.add('ohs-open');
      btn.setAttribute('aria-expanded', 'true');
      const input = field();
      if (input) {
        input.focus();
        input.dispatchEvent(new Event('focus', { bubbles: true }));
      }
    };

    const close = () => {
      document.body.classList.remove('ohs-open');
      btn.setAttribute('aria-expanded', 'false');
    };

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isOpen()) close();
      else open();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen()) close();
    });

    // Tap away to dismiss — but never while a query is half-typed.
    document.addEventListener('click', (e) => {
      if (!isOpen()) return;
      if (wrap.contains(e.target) || btn.contains(e.target)) return;
      const input = field();
      if (input && input.value.trim() !== '') return;
      close();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
